import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, Sparkles, ArrowLeft, Mail, MessageCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIPricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [contact, setContact] = useState({});
  const [planStatus, setPlanStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: pub } = await axios.get(`${API}/ai-plans/public`);
      setPlans(pub.plans || []);
      setContact(pub.contact || {});
      if (user) {
        try {
          const { data: status } = await axios.get(`${API}/admin/ai-plan-status`, { withCredentials: true });
          setPlanStatus(status);
        } catch { /* user isn't admin */ }
      }
    } catch (e) {
      toast.error('Errore caricamento piani');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const contactClick = (channel) => {
    if (channel === 'email' && contact.support_email) {
      window.location.href = `mailto:${contact.support_email}?subject=Attivazione%20Piano%20AI%20PhotoVendo`;
    } else if (channel === 'whatsapp' && contact.support_whatsapp) {
      const n = contact.support_whatsapp.replace(/\D/g, '');
      window.open(`https://wa.me/${n}?text=${encodeURIComponent('Ciao, vorrei attivare il piano AI PhotoVendo per il mio negozio.')}`, '_blank');
    } else if (channel === 'telegram' && contact.support_telegram) {
      const handle = contact.support_telegram.replace(/^@/, '');
      window.open(`https://t.me/${handle}`, '_blank');
    } else {
      toast.info('Canale non configurato — contatta SuperAdmin');
    }
  };

  if (loading) return <div className="px-4 pt-8 text-center"><Loader2 size={32} className="mx-auto animate-spin text-[#C25934]" /></div>;

  return (
    <div data-testid="ai-pricing-page" className="px-4 pt-4 pb-safe space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-[#F0EBE1]" data-testid="back-btn">
          <ArrowLeft size={20} className="text-[#5C554F]" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>
            <Sparkles size={20} className="inline text-[#C25934] mr-1" /> Piani AI
          </h1>
          <p className="text-xs text-[#877E78]">Attiva la generazione automatica delle foto prodotto</p>
        </div>
      </div>

      {/* Current plan status badge */}
      {planStatus && (
        <div className={`rounded-xl p-3 border ${planStatus.active ? 'bg-[#4A6B53]/5 border-[#4A6B53]/30' : 'bg-amber-50 border-amber-300'}`} data-testid="current-plan-status">
          <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: planStatus.active ? '#4A6B53' : '#b45309' }}>
            {planStatus.active ? 'Piano AI attivo' : 'Piano AI non attivo'}
          </p>
          {planStatus.active && planStatus.plan ? (
            <p className="text-sm text-[#5C554F]">
              <b>{planStatus.plan.name}</b> — {planStatus.plan.used}/{planStatus.plan.included} elaborazioni usate · scade il {new Date(planStatus.plan.expires_at).toLocaleDateString('it-IT')}
            </p>
          ) : (
            <p className="text-sm text-[#5C554F]">
              {planStatus.reason === 'expired' && 'Il piano è scaduto. Contatta il supporto per rinnovare.'}
              {planStatus.reason === 'quota_exceeded' && 'Hai raggiunto il limite elaborazioni del piano. Contatta il supporto per upgrade.'}
              {planStatus.reason === 'no_active_plan' && 'Scegli un piano e contatta il supporto per attivarlo.'}
              {planStatus.reason === 'no_shop' && 'Nessun negozio associato al tuo account.'}
            </p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {plans.length === 0 && (
          <div className="bg-white rounded-2xl border border-[#E8E3D9] p-6 text-center text-[#877E78]">
            Nessun piano disponibile al momento.
          </div>
        )}
        {plans.map(p => (
          <div key={p.id} data-testid={`plan-${p.id}`} className="bg-white rounded-2xl border border-[#E8E3D9] p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <h2 className="text-xl font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>{p.name}</h2>
              <div className="text-right">
                <span className="text-2xl font-bold text-[#C25934]">€{p.price_monthly?.toFixed(2)}</span>
                <span className="text-xs text-[#877E78]">/mese</span>
              </div>
            </div>
            {p.price_yearly ? (
              <p className="text-xs text-[#877E78]">oppure <b>€{p.price_yearly.toFixed(2)}/anno</b> (sconto equivalente {Math.round(100 - (p.price_yearly / (p.price_monthly * 12)) * 100)}%)</p>
            ) : null}
            <div className="bg-[#F7F3EC] rounded-xl p-2.5 text-xs text-[#5C554F]">
              <b>{p.included_elaborations}</b> elaborazioni AI incluse · rinnovo ogni <b>{p.duration_days} giorni</b>
            </div>
            <ul className="space-y-1.5 text-sm text-[#5C554F]">
              {(p.features || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check size={16} className="text-[#4A6B53] mt-0.5 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            <div className="pt-1 border-t border-[#E8E3D9]">
              <p className="text-[10px] text-[#877E78] mb-2 text-center">Per attivare, contatta il supporto:</p>
              <div className="grid grid-cols-3 gap-2">
                {contact.support_whatsapp && (
                  <Button data-testid={`whatsapp-${p.id}`} onClick={() => contactClick('whatsapp')} variant="outline" className="h-10 rounded-full border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/5 text-xs">
                    <MessageCircle size={14} className="mr-1" /> WA
                  </Button>
                )}
                {contact.support_telegram && (
                  <Button data-testid={`telegram-${p.id}`} onClick={() => contactClick('telegram')} variant="outline" className="h-10 rounded-full border-[#0088cc]/40 text-[#0088cc] hover:bg-[#0088cc]/5 text-xs">
                    <Send size={14} className="mr-1" /> TG
                  </Button>
                )}
                {contact.support_email && (
                  <Button data-testid={`email-${p.id}`} onClick={() => contactClick('email')} variant="outline" className="h-10 rounded-full border-[#C25934]/40 text-[#C25934] hover:bg-[#C25934]/5 text-xs">
                    <Mail size={14} className="mr-1" /> Email
                  </Button>
                )}
                {!contact.support_whatsapp && !contact.support_telegram && !contact.support_email && (
                  <p className="col-span-3 text-xs text-center text-[#877E78] py-2">
                    Contatti supporto non configurati — contatta il SuperAdmin della piattaforma.
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
